
/**
 * CrisisEvent Integration Layer
 * Connects SDK Service to Zustand Store
 */
import { toast } from 'sonner';
import i18next from 'i18next';
import { useCrisisEventStore } from '../../store/crisis-event.store.js';
import { createTeamAssociationIntegrationMethods } from './team-association.integration-mixin.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class CrisisEventIntegration {
    constructor(sdkService) {
        this.service = sdkService;
        Object.assign(this, createTeamAssociationIntegrationMethods({
            afterMutate: this._afterCrisisAssociationMutation,
        }));
    }

    _patchSelectedCrisisIfCurrent(crisisId, data) {
        if (!crisisId || !data) return;
        const store = useCrisisEventStore.getState();
        const current = store.selectedCrisis || store.selectedItem;
        const currentId = current?.id || current?._id;
        if (currentId && String(currentId) === String(crisisId)) {
            if (store.setSelectedCrisis) store.setSelectedCrisis(data);
            else if (store.setSelectedItem) store.setSelectedItem(data);
        }
    }

    syncAfterAssociationMutation(crisisId, data) {
        this._patchSelectedCrisisIfCurrent(crisisId, data);
    }

    _afterCrisisAssociationMutation(crisisId, data) {
        this.syncAfterAssociationMutation(crisisId, data);
    }

    /**
     * Fetch Crisis Events
     */
    async fetchCrisisEvents(page = 1, pageSize = 10, search = '', sortBy = 'title', sortOrder = 'asc', filters = {}) {
        return this._fetchItems({ page, pageSize, search, sortBy, sortOrder, ...filters }, 'CRISIS');
    }

    async fetchStats() {
        const store = useCrisisEventStore.getState();
        if (store.setLoadingStats) store.setLoadingStats(true);

        try {
            const response = this.service.getStats
                ? await this.service.getStats()
                : { data: null };
            if (store.setStats) store.setStats(response.data);
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: error.message };
        } finally {
            if (store.setLoadingStats) store.setLoadingStats(false);
        }
    }

    /**
     * Fetch Recovery Activities
     */
    async fetchRecoveryActivities() {
        return { success: false, error: 'Use CrisisRecoveryActivitiesIntegration' };
    }

    /**
     * Fetch Improvement Actions
     */
    async fetchImprovementActions() {
        return { success: false, error: 'Use CrisisImprovementActionsIntegration' };
    }

    /**
     * Fetch single Crisis Event by ID
     */
    async fetchCrisisEventById(id) {
        const store = useCrisisEventStore.getState();

        if (store.setLoadingDetail) store.setLoadingDetail(true);
        if (store.clearError) store.clearError();

        try {
            const response = await this.service.getById(id);

            if (store.setSelectedCrisis) {
                store.setSelectedCrisis(response.data);
            } else if (store.setSelectedItem) {
                store.setSelectedItem(response.data);
            }

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            if (store.setError) store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoadingDetail) store.setLoadingDetail(false);
        }
    }

    async _fetchItems(params, type) {
        const store = useCrisisEventStore.getState();

        let setLoading, setList, setPagination;

        if (type === 'CRISIS') {
            setLoading = store.setLoadingCrises;
            setList = store.setCrisisEvents;
            setPagination = store.setPaginationCrises;
        } else if (type === 'RECOVERY') {
            setLoading = store.setLoadingRecovery;
            setList = store.setRecoveryActivities;
            setPagination = store.setPaginationRecovery;
        } else if (type === 'IMPROVEMENT') {
            setLoading = store.setLoadingImprovement;
            setList = store.setImprovementActions;
            setPagination = store.setPaginationImprovement;
        }

        if (setLoading) setLoading(true);
        if (store.clearError) store.clearError();

        try {
            const response = await this.service.getAll(params);

            if (setList) setList(response.data);
            store.setUniqueValues?.(response.uniqueValues || {});
            const pagination = response.pagination || response.meta;
            if (pagination && setPagination) {
                const currentPage = parseInt(pagination.page || pagination.currentPage || 1);
                const totalPages = parseInt(pagination.totalPages || 1);
                setPagination({
                    currentPage: currentPage,
                    itemsPerPage: parseInt(pagination.pageSize || pagination.itemsPerPage || 10),
                    totalItems: parseInt(pagination.total || pagination.totalItems || 0),
                    totalPages: totalPages,
                    hasNextPage: currentPage < totalPages,
                    hasPreviousPage: currentPage > 1
                });
            }

            return { success: true, data: response.data, uniqueValues: response.uniqueValues || {} };
        } catch (error) {
            const message = this._getErrorMessage(error);
            if (store.setError) store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (setLoading) setLoading(false);
        }
    }

    /**
     * CRUD Operations
     */
    async createCrisisEvent(data) {
        return this._execute(
            async () => await this.service.create(data),
            i18next.t('bcmCrisisEvent:messages.createSuccess', { defaultValue: 'Crisis event successfully created' }),
        );
    }

    async updateCrisisEvent(id, data) {
        return this._execute(
            async () => await this.service.update(id, data),
            i18next.t('bcmCrisisEvent:messages.updateSuccess', { defaultValue: 'Crisis event successfully updated' }),
        );
    }

    async deleteCrisisEvent(id) {
        return this._execute(
            async () => await this.service.delete(id),
            i18next.t('bcmCrisisEvent:messages.deleteSuccess', { defaultValue: 'Crisis event successfully deleted' }),
        );
    }

    /**
     * Bulk Delete
     */
    async bulkDeleteCrisisEvents(ids) {
        return this._executeBatch(async () => await this.service.batchDelete(ids));
    }

    

    /**
     * Helper to execute operations with standard handling
     */
    async _execute(operation, successMessage) {
        const store = useCrisisEventStore.getState();
        if (store.setIsSubmitting) store.setIsSubmitting(true);

        try {
            const response = await operation();
            toast.success(successMessage);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setIsSubmitting) store.setIsSubmitting(false);
        }
    }

    /**
     * Helper for batch operations
     */
    async _executeBatch(operation) {
        const store = useCrisisEventStore.getState();
        if (store.setIsSubmitting) store.setIsSubmitting(true);

        try {
            const response = await operation();
            const count = response.deletedCount ?? response.data?.deletedCount;
            const msg = count !== undefined
                ? i18next.t('integrationToasts:itemsDeletedSuccessfully', {
                    count,
                    defaultValue: `${count} items deleted successfully`,
                })
                : i18next.t('integrationToasts:itemsDeleted', { defaultValue: 'Items deleted' });
            toast.success(msg);
            return { success: true, deletedCount: count };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setIsSubmitting) store.setIsSubmitting(false);
        }
    }

    // --- Activated BCPs Relation Methods ---

    async fetchRelatedBcps(crisisId, params = {}) {
        try {
            const response = await this.service.getRelatedBcps(crisisId, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
                uniqueValues: response.uniqueValues || {}
            };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async addBcps(crisisId, bcpIds, opts = {}) {
        try {
            const response = await this.service.addBcps(crisisId, bcpIds);
            this._afterCrisisAssociationMutation(crisisId, response.data);
            if (!opts.silent) {
                toast.success(i18next.t('bcmCrisisEvent:messages.bcpsLinkedSuccess', { defaultValue: 'BCPs linked successfully' }));
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async removeBcp(crisisId, bcpId, opts = {}) {
        try {
            const response = await this.service.removeBcp(crisisId, bcpId);
            this._afterCrisisAssociationMutation(crisisId, response.data);
            if (!opts.silent) {
                toast.success(i18next.t('bcmCrisisEvent:messages.bcpUnlinkedSuccess', { defaultValue: 'BCP unlinked successfully' }));
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async fetchRelatedBias(crisisId, params = {}) {
        try {
            const response = await this.service.getRelatedBias(crisisId, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
            };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async addBias(crisisId, biaIds, opts = {}) {
        try {
            const response = await this.service.addBias(crisisId, biaIds);
            this._afterCrisisAssociationMutation(crisisId, response.data);
            if (!opts.silent) {
                toast.success(i18next.t('bcmCrisisEvent:messages.biasLinkedSuccess', { defaultValue: 'BIAs linked successfully' }));
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async removeBia(crisisId, biaId, opts = {}) {
        try {
            const response = await this.service.removeBia(crisisId, biaId);
            this._afterCrisisAssociationMutation(crisisId, response.data);
            if (!opts.silent) {
                toast.success(i18next.t('bcmCrisisEvent:messages.biaUnlinkedSuccess', { defaultValue: 'BIA unlinked successfully' }));
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    // --- Processes Relation Methods ---
    async fetchRelatedProcesses(crisisId, params = {}) {
        try {
            const response = await this.service.getRelatedProcesses(crisisId, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {} };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }
    async addProcesses(crisisId, processIds, opts = {}) {
        try {
            const response = await this.service.addProcesses(crisisId, processIds);
            this._afterCrisisAssociationMutation(crisisId, response.data);
            if (!opts.silent) {
                toast.success(i18next.t('bcmCrisisEvent:messages.processesLinkedSuccess', { defaultValue: 'Processes linked successfully' }));
            }
            return { success: true, data: response.data };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }
    async removeProcess(crisisId, processId) {
        try {
            const response = await this.service.removeProcess(crisisId, processId);
            this._afterCrisisAssociationMutation(crisisId, response.data);
            return { success: true, data: response.data };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }

    // --- Assets Relation Methods ---
    async fetchRelatedAssets(crisisId, params = {}) {
        try {
            const response = await this.service.getRelatedAssets(crisisId, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {} };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }
    async addAssets(crisisId, assetIds, opts = {}) {
        try {
            const response = await this.service.addAssets(crisisId, assetIds);
            this._afterCrisisAssociationMutation(crisisId, response.data);
            if (!opts.silent) {
                toast.success(i18next.t('bcmCrisisEvent:messages.assetsLinkedSuccess', { defaultValue: 'Assets linked successfully' }));
            }
            return { success: true, data: response.data };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }
    async removeAsset(crisisId, assetId) {
        try {
            const response = await this.service.removeAsset(crisisId, assetId);
            this._afterCrisisAssociationMutation(crisisId, response.data);
            return { success: true, data: response.data };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }

    // --- Vendors Relation Methods ---
    async fetchRelatedVendors(crisisId, params = {}) {
        try {
            const response = await this.service.getRelatedVendors(crisisId, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {} };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }
    async addVendors(crisisId, vendorIds, opts = {}) {
        try {
            const response = await this.service.addVendors(crisisId, vendorIds);
            this._afterCrisisAssociationMutation(crisisId, response.data);
            if (!opts.silent) {
                toast.success(i18next.t('bcmCrisisEvent:messages.vendorsLinkedSuccess', { defaultValue: 'Vendors linked successfully' }));
            }
            return { success: true, data: response.data };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }
    async removeVendor(crisisId, vendorId) {
        try {
            const response = await this.service.removeVendor(crisisId, vendorId);
            this._afterCrisisAssociationMutation(crisisId, response.data);
            return { success: true, data: response.data };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }

    // --- Communications Relation Methods ---
    async fetchRelatedCommunications(crisisId, params = {}) {
        try {
            const response = await this.service.getRelatedCommunications(crisisId, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {} };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }
    async addCommunications(crisisId, communicationIds) {
        try {
            const response = await this.service.addCommunications(crisisId, communicationIds);
            this._afterCrisisAssociationMutation(crisisId, response.data);
            toast.success(i18next.t('integrationToasts:communicationsLinkedSuccessfully', { defaultValue: 'Communications linked successfully' }));
            return { success: true, data: response.data };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }
    async removeCommunication(crisisId, communicationId) {
        try {
            const response = await this.service.removeCommunication(crisisId, communicationId);
            this._afterCrisisAssociationMutation(crisisId, response.data);
            return { success: true, data: response.data };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }

    // --- Communication Plans Relation Methods ---
    async fetchRelatedCommunicationPlans(crisisId, params = {}) {
        try {
            const response = await this.service.getRelatedCommunicationPlans(crisisId, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {} };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }
    async addCommunicationPlans(crisisId, planIds) {
        try {
            const response = await this.service.addCommunicationPlans(crisisId, planIds);
            this._afterCrisisAssociationMutation(crisisId, response.data);
            toast.success(i18next.t('integrationToasts:communicationPlansLinkedSuccessfully', {
                defaultValue: 'Communication plans linked successfully',
            }));
            return { success: true, data: response.data };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }
    async removeCommunicationPlan(crisisId, planId) {
        try {
            const response = await this.service.removeCommunicationPlan(crisisId, planId);
            this._afterCrisisAssociationMutation(crisisId, response.data);
            return { success: true, data: response.data };
        } catch (error) { toast.error(this._getErrorMessage(error)); return { success: false, error: this._getErrorMessage(error) }; }
    }

    // --- Recovery Resources Relation Methods ---
    async fetchRelatedRecoveryResources(crisisId, params = {}) {
        try {
            const response = await this.service.getRelatedRecoveryResources(crisisId, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {} };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false, data: [] };
        }
    }
    async addRecoveryResources(crisisId, resourceIds) {
        try {
            const response = await this.service.addRecoveryResources(crisisId, resourceIds);
            this._afterCrisisAssociationMutation(crisisId, response.data);
            toast.success(i18next.t('integrationToasts:recoveryResourcesLinked', { defaultValue: 'Recovery resources linked' }));
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false };
        }
    }
    async removeRecoveryResource(crisisId, resourceId) {
        try {
            const response = await this.service.removeRecoveryResource(crisisId, resourceId);
            this._afterCrisisAssociationMutation(crisisId, response.data);
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false };
        }
    }

    // --- Recovery Activities Relation Methods ---
    async fetchRelatedRecoveryActivities(crisisId, params = {}) {
        try {
            const response = await this.service.getRelatedRecoveryActivities(crisisId, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {} };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false, data: [] };
        }
    }
    async addRecoveryActivities(crisisId, activityIds) {
        try {
            const response = await this.service.addRecoveryActivities(crisisId, activityIds);
            this._afterCrisisAssociationMutation(crisisId, response.data);
            toast.success(i18next.t('integrationToasts:recoveryActivitiesLinked', { defaultValue: 'Recovery activities linked' }));
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false };
        }
    }
    async removeRecoveryActivity(crisisId, activityId) {
        try {
            const response = await this.service.removeRecoveryActivity(crisisId, activityId);
            this._afterCrisisAssociationMutation(crisisId, response.data);
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false };
        }
    }

    async reorderRecoveryActivities(crisisId, orderedActivityIds) {
        try {
            const response = await this.service.reorderRecoveryActivities(crisisId, orderedActivityIds);
            toast.success(i18next.t('integrationToasts:recoveryActivitiesReordered', { defaultValue: 'Recovery activities reordered' }));
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false };
        }
    }

    // --- Lessons Learned Relation Methods ---
    async fetchRelatedLessonsLearned(crisisId, params = {}) {
        try {
            const response = await this.service.getRelatedLessonsLearned(crisisId, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {} };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false, data: [] };
        }
    }
    async addLessonsLearned(crisisId, lessonIds) {
        try {
            const response = await this.service.addLessonsLearned(crisisId, lessonIds);
            this._afterCrisisAssociationMutation(crisisId, response.data);
            toast.success(i18next.t('integrationToasts:lessonsLinked', { defaultValue: 'Lessons linked' }));
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false };
        }
    }
    async removeLessonLearned(crisisId, lessonId, opts = {}) {
        try {
            const response = await this.service.removeLessonLearned(crisisId, lessonId);
            this._afterCrisisAssociationMutation(crisisId, response.data);
            if (!opts.silent) {
                toast.success(i18next.t('integrationToasts:lessonUnlinked', { defaultValue: 'Lesson unlinked' }));
            }
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false };
        }
    }

    // --- Improvement Actions Relation Methods ---
    async fetchRelatedImprovementActions(crisisId, params = {}) {
        try {
            const response = await this.service.getRelatedImprovementActions(crisisId, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {} };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false, data: [] };
        }
    }
    async addImprovementActions(crisisId, actionIds, extra = {}) {
        try {
            const response = await this.service.addImprovementActions(crisisId, actionIds, extra);
            this._afterCrisisAssociationMutation(crisisId, response.data);
            toast.success(i18next.t('bcmCrisisEvent:messages.improvementAction.linked', { defaultValue: 'Improvement actions linked' }));
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false };
        }
    }
    async getLinkableImprovementActions(crisisId, params = {}) {
        try {
            const response = await this.service.getLinkableImprovementActions(crisisId, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {} };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false, data: [] };
        }
    }
    async unlinkImprovementActions(crisisId, actionIds, opts = {}) {
        try {
            const response = await this.service.unlinkImprovementActions(crisisId, actionIds);
            this._afterCrisisAssociationMutation(crisisId, response.data);
            if (!opts.silent) {
                toast.success(i18next.t('bcmCrisisEvent:messages.improvementAction.unlinked', { defaultValue: 'Improvement action unlinked' }));
            }
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false };
        }
    }
    async removeImprovementAction(crisisId, actionId, opts = {}) {
        try {
            const response = await this.service.removeImprovementAction(crisisId, actionId);
            this._afterCrisisAssociationMutation(crisisId, response.data);
            if (!opts.silent) {
                toast.success(i18next.t('bcmCrisisEvent:messages.improvementAction.unlinked', { defaultValue: 'Improvement action unlinked' }));
            }
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false };
        }
    }

    async fetchCrisisParty(crisisId) {
        try {
            const response = await this.service.getParty(crisisId);
            return {
                success: response.success !== false,
                data: response.data,
                partyTemplate: response.partyTemplate ?? null,
            };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false, data: null, partyTemplate: null };
        }
    }

    async syncCrisisParty(crisisId, payload, options = {}) {
        const { silentSuccess = false } = options;
        try {
            const response = await this.service.syncParty(crisisId, payload);
            if (!silentSuccess) {
                toast.success(i18next.t('bcmCrisisEvent:messages.partySaveSuccess', { defaultValue: 'Ownership and roles saved' }));
            }
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false };
        }
    }

    async activateBcp(crisisId, bcpId) {
        try {
            const response = await this.service.activateBcp(crisisId, bcpId);
            toast.success(i18next.t('bcmCrisisEvent:messages.bcpActivated', { defaultValue: 'BCP activated' }));
            this._afterCrisisAssociationMutation(crisisId, response.data);
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false };
        }
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error);
    }
}
